import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

const GEO_PERMISSION_KEY = 'plp_geo_granted';

@Injectable({
  providedIn: 'root',
})
export class GrantedPermitionsService {
  /** null = aún no se ha verificado; evita disparar el modal antes de la verificación real. */
  public estadoPermiso$ = new BehaviorSubject<string | null>(null);

  constructor(private ngZone: NgZone) {
    // Si ya se otorgó antes, emitir 'granted' de inmediato (evita re-pedir en cada recarga)
    if (this.hasCachedGrant()) {
      this.emitirEstado('granted');
    }
    this.vigilarPermiso();
  }

  /** Actualiza el estado desde fuera (p. ej. modal de ubicación); se ejecuta dentro de NgZone. */
  public actualizarEstado(nuevoEstado: string): void {
    this.persistirEstado(nuevoEstado);
    this.ngZone.run(() => this.estadoPermiso$.next(nuevoEstado));
  }

  private emitirEstado(estado: string): void {
    this.persistirEstado(estado);
    this.ngZone.run(() => this.estadoPermiso$.next(estado));
  }

  private hasCachedGrant(): boolean {
    try { return localStorage.getItem(GEO_PERMISSION_KEY) === 'true'; } catch { return false; }
  }

  private persistirEstado(estado: string): void {
    try {
      if (estado === 'granted') {
        localStorage.setItem(GEO_PERMISSION_KEY, 'true');
      } else if (estado === 'denied') {
        localStorage.removeItem(GEO_PERMISSION_KEY);
      }
    } catch {}
  }

  private async vigilarPermiso(): Promise<void> {
    if (!navigator.geolocation) {
      this.emitirEstado('prompt');
      return;
    }
    if (!navigator.permissions?.query) {
      // Solo verificar manualmente si no tenemos un grant cacheado
      if (!this.hasCachedGrant()) {
        this.verificacionManualIOS();
      }
      return;
    }

    try {
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });

      // Solo actualizar si el browser dice algo diferente a nuestro cache
      if (!this.hasCachedGrant() || status.state === 'denied') {
        this.emitirEstado(status.state);
      }

      status.onchange = () => {
        this.persistirEstado(status.state);
        this.ngZone.run(() => this.estadoPermiso$.next(status.state));
      };
    } catch {
      if (!this.hasCachedGrant()) {
        this.verificacionManualIOS();
      }
    }
  }

  private verificacionManualIOS(): void {
    if (!navigator.geolocation) {
      this.emitirEstado('prompt');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => this.emitirEstado('granted'),
      (err: GeolocationPositionError) => {
        if (err.code === 1) {
          this.emitirEstado('denied');
        } else {
          this.emitirEstado('prompt');
        }
      },
      { timeout: 2000 },
    );
  }
}